/** @format */
import { inject, Injectable } from '@angular/core';
import { StudyPeriodLocator } from 'common-typescript/types';
import { omit, orderBy } from 'lodash-es';
import { map, Observable, shareReplay } from 'rxjs';
import { StudyPeriodData, StudyRightExpiredInfo, StudyTermData, TimelineItem } from '../../timing.types';
import { StudyPeriodDataService } from '../study-period-data.service';

/**
 * Represents the position of an item in a CSS Grid layout. Indexing starts at 1, start indices are inclusive,
 * and end indices are exclusive. E.g. an item positioned in the top left cell of a grid would have
 * `rowStart = 1`, `rowEnd = 2`, `columnStart = 1`, and `columnEnd = 2`.
 */
export interface GridArea {
    readonly columnStart: number;
    readonly columnEnd: number;
    readonly rowStart: number;
    readonly rowEnd: number;
}

export interface PositionedTimelineItem extends TimelineItem, GridArea {}

export interface PositionedStudyPeriodData extends Omit<StudyPeriodData, 'timelineItems'>, GridArea {
    /**
     * Whether the period has any timed/attained studies (either starting during the period or continuing from a previous one).
     * Note that this is different from {@link HorizontalTimelineData.timelineItemsByStartingPeriod}, which only contains items
     * that start during each period.
     */
    readonly hasTimedStudies: boolean;
}

export interface PositionedStudyTermData extends Omit<StudyTermData, 'periods' | 'studyRightExpiredInfo'>, GridArea {}

export interface PositionedStudyRightExpiredInfo extends StudyRightExpiredInfo, GridArea {}

export interface HorizontalTimelineData {
    readonly studyTerms: readonly PositionedStudyTermData[];
    readonly studyPeriods: readonly PositionedStudyPeriodData[];
    readonly studyRightExpiredInfo?: PositionedStudyRightExpiredInfo;
    /**
     * Contains all visible timeline items, grouped by the period in which they start. Items that have been timed to multiple consecutive
     * periods will only appear in the group for the first period as a single stretched item. Items that have been timed to multiple
     * non-consecutive periods will be split into multiple items, and each item will appear in the group for the period in which it starts.
     */
    readonly timelineItemsByStartingPeriod: ReadonlyMap<StudyPeriodLocator, readonly PositionedTimelineItem[]>;
    readonly timelineItemsByContinueePeriod: ReadonlyMap<StudyPeriodLocator, readonly PositionedTimelineItem[]>;
}

const STUDY_TERM_ROW_INDEX = 1;
const STUDY_RIGHT_EXPIRED_INFO_ROW_INDEX = 2;
const STUDY_PERIOD_ROW_INDEX = 3;
/** Heading, subheading, credits and the edit button of a study period */
const STUDY_PERIOD_HEADING_ROW_COUNT = 4;
const TIMELINE_ITEM_ROW_INDEX_START = STUDY_PERIOD_ROW_INDEX + STUDY_PERIOD_HEADING_ROW_COUNT;

@Injectable()
export class HorizontalTimelineService {
    private readonly studyPeriodDataService = inject(StudyPeriodDataService);

    readonly positionedStudyTerms$: Observable<HorizontalTimelineData> = this.studyPeriodDataService.studyTermData$.pipe(
        map(studyTerms => {
            const { positionedItemsByStartingPeriod, positionedItemsByContinueePeriod } = this.createPositionedTimelineItems(studyTerms);
            const { positionedTerms, positionedPeriods } = this.createPositionedTermsAndPeriods(
                studyTerms,
                positionedItemsByStartingPeriod,
            );

            return {
                studyTerms: positionedTerms,
                studyPeriods: positionedPeriods,
                studyRightExpiredInfo: this.createPositionedStudyRightExpiredInfo(studyTerms),
                timelineItemsByStartingPeriod: positionedItemsByStartingPeriod,
                timelineItemsByContinueePeriod: positionedItemsByContinueePeriod,
            };
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private createPositionedTermsAndPeriods(
        studyTerms: readonly StudyTermData[],
        timelineItemsByStartingPeriod: ReadonlyMap<StudyPeriodLocator, readonly PositionedTimelineItem[]>,
    ): {
        positionedTerms: PositionedStudyTermData[];
        positionedPeriods: PositionedStudyPeriodData[];
    } {
        const positionedTerms: PositionedStudyTermData[] = [];
        const positionedPeriods: PositionedStudyPeriodData[] = [];
        const positionedItems = Array.from(timelineItemsByStartingPeriod.values()).flat();
        let currentColumn = 1;
        studyTerms.forEach(term => {
            positionedTerms.push({
                ...omit(term, 'periods', 'studyRightExpiredInfo'),
                rowStart: STUDY_TERM_ROW_INDEX,
                columnStart: currentColumn,
                rowEnd: STUDY_TERM_ROW_INDEX + 1,
                columnEnd: currentColumn + term.periods.length,
            });
            term.periods.forEach((period, periodIndex) =>
                positionedPeriods.push({
                    ...omit(period, 'timelineItems'),
                    rowStart: STUDY_PERIOD_ROW_INDEX,
                    columnStart: currentColumn + periodIndex,
                    rowEnd: this.getStudyPeriodRowEnd(currentColumn + periodIndex, positionedItems),
                    columnEnd: currentColumn + periodIndex + 1,
                    hasTimedStudies: period.timelineItems?.length > 0,
                }),
            );

            currentColumn += term.periods.length;
        });

        return { positionedTerms, positionedPeriods };
    }

    /**
     * A study period covers its whole column: the heading rows and the rows of the timeline items placed in the column.
     */
    private getStudyPeriodRowEnd(column: number, positionedItems: readonly PositionedTimelineItem[]): number {
        const itemRowEnds = positionedItems.filter(item => item.columnStart <= column && column < item.columnEnd).map(item => item.rowEnd);
        // An empty study period has a single row for the empty state text
        return itemRowEnds.length > 0 ? Math.max(...itemRowEnds) : TIMELINE_ITEM_ROW_INDEX_START + 1;
    }

    private createPositionedStudyRightExpiredInfo(studyTerms: readonly StudyTermData[]): PositionedStudyRightExpiredInfo | undefined {
        let columnStart = 1;
        let firstExpiryInfo: StudyRightExpiredInfo;
        for (const studyTerm of studyTerms) {
            if (studyTerm.studyRightExpiredInfo) {
                firstExpiryInfo = studyTerm.studyRightExpiredInfo;
                break;
            }
            columnStart += studyTerm.periods.length;
        }

        if (firstExpiryInfo) {
            const totalColumnCount = studyTerms.reduce((total, term) => total + (term.periods?.length ?? 0), 0);
            return {
                ...firstExpiryInfo,
                rowStart: STUDY_RIGHT_EXPIRED_INFO_ROW_INDEX,
                columnStart,
                rowEnd: STUDY_RIGHT_EXPIRED_INFO_ROW_INDEX + 1,
                columnEnd: totalColumnCount + 1,
            };
        }

        return undefined;
    }

    private createPositionedTimelineItems(studyTerms: readonly StudyTermData[]): {
        positionedItemsByStartingPeriod: ReadonlyMap<StudyPeriodLocator, readonly PositionedTimelineItem[]>;
        positionedItemsByContinueePeriod: ReadonlyMap<StudyPeriodLocator, readonly PositionedTimelineItem[]>;
    } {
        const allTimelineItems = studyTerms
            .flatMap(term => term.periods.flatMap(period => period.timelineItems ?? []))
            .filter(item => item.placedInPeriods?.length > 0)
            // Timeline items in the source data are duplicated to all periods they are placed in, so we need to deduplicate them here
            .reduce((acc, item) => (acc.some(({ id }) => id === item.id) ? acc : [...acc, item]), []);

        // Sort timeline items first by the earliest period they are placed in, and then by the number of periods they are placed in (descending).
        // The sorting counts all periods the item is placed in, regardless of whether all periods are visible or not. This ensures that items
        // are consistently placed on the same rows regardless of which periods are currently visible in the timeline.
        const sortedTimelineItems = orderBy(
            allTimelineItems,
            [item => item.placedInPeriods.toSorted()[0], item => item.placedInPeriods?.length ?? 0],
            ['asc', 'desc'],
        );

        const visiblePeriodLocators = studyTerms.flatMap(term => term.periods.map(period => period.locator));
        const positionedItemsByStartingPeriod = new Map<StudyPeriodLocator, PositionedTimelineItem[]>(
            visiblePeriodLocators.map(locator => [locator, <PositionedTimelineItem[]>[]]),
        );
        // Item placements of long timings, not including the starting period.
        const positionedItemsByContinueePeriod = new Map<StudyPeriodLocator, PositionedTimelineItem[]>(
            visiblePeriodLocators.map(locator => [locator, <PositionedTimelineItem[]>[]]),
        );

        // placementGrid is a 2D array that tracks which cells in the CSS Grid layout are occupied by timeline items.
        // Note that this "grid" does not contain the study term and period header sections, but only the timeline item section.
        // A `true` value indicates that the cell is occupied, and a falsy value indicates that the cell is free.
        // Gaps between non-consecutive placements of the same item are also marked as occupied to simplify the placement logic.
        const placementGrid: boolean[][] = Array.from({ length: visiblePeriodLocators.length }, (): boolean[] => []);

        sortedTimelineItems.forEach(item => {
            const placedColumnIndices = visiblePeriodLocators.flatMap((locator, columnIndex) =>
                item.placedInPeriods.includes(locator) ? [columnIndex] : [],
            );
            if (placedColumnIndices.length === 0) {
                return;
            }
            const firstPlacementColumnIndex = placedColumnIndices[0];
            const lastPlacementColumnIndex = placedColumnIndices[placedColumnIndices.length - 1];
            // All placements of an item are on the same row, which must be free over the full length of the placement, including gaps.
            // Checking only the first column is not enough, because the sorting also counts periods outside the visible timeline.
            // E.g. an item timed to a hidden period and the second visible period is placed before an item that starts in the first one.
            const rowIndex = this.findFreeRowIndex(placementGrid, firstPlacementColumnIndex, lastPlacementColumnIndex);

            let ongoingPlacement = false;
            let columnIndexStart: number | undefined;
            let columnIndexEnd: number | undefined;
            let firstPlacementPeriodLocator: StudyPeriodLocator | undefined;
            let latterPlacementPeriodLocators: StudyPeriodLocator[] = [];
            for (let columnIndex = 0; columnIndex < visiblePeriodLocators.length; columnIndex += 1) {
                const locator = visiblePeriodLocators[columnIndex];
                const isPlacedInPeriod = item.placedInPeriods.includes(locator);
                if (isPlacedInPeriod) {
                    if (ongoingPlacement) {
                        // Long placement, continue to next column
                        latterPlacementPeriodLocators.push(locator);
                        columnIndexEnd = columnIndex + 1;
                    } else {
                        // Start new placement
                        ongoingPlacement = true;
                        firstPlacementPeriodLocator = locator;
                        columnIndexStart = columnIndex;
                        columnIndexEnd = columnIndexStart + 1;
                    }
                }

                const isLastColumn = columnIndex === visiblePeriodLocators.length - 1;
                if ((!isPlacedInPeriod || isLastColumn) && ongoingPlacement) {
                    // Placement ended (or end of visible timeline reached), finalize the placement.
                    // CSS Grid indexing starts at 1, so add 1 to the 0-based indices used during placement calculations.
                    const columnStart = columnIndexStart + 1;
                    const columnEnd = columnIndexEnd + 1;
                    const positionedTimelineItem = {
                        ...item,
                        rowStart: TIMELINE_ITEM_ROW_INDEX_START + rowIndex,
                        columnStart,
                        rowEnd: TIMELINE_ITEM_ROW_INDEX_START + rowIndex + 1,
                        columnEnd,
                    };
                    positionedItemsByStartingPeriod.get(firstPlacementPeriodLocator).push(positionedTimelineItem);

                    if (item.placedInPeriods.length === 1) {
                        // Single placement, finalize the placement immediately
                        break;
                    }

                    latterPlacementPeriodLocators.forEach(latterLocator =>
                        positionedItemsByContinueePeriod.get(latterLocator).push(positionedTimelineItem),
                    );

                    ongoingPlacement = false;
                    firstPlacementPeriodLocator = undefined;
                    latterPlacementPeriodLocators = [];
                    columnIndexStart = undefined;
                    columnIndexEnd = undefined;
                }
            }

            // Mark all cells in the placement grid as occupied for the full length of the placement, including gaps.
            // This keeps other items out of the gaps.
            for (let columnIndex = firstPlacementColumnIndex; columnIndex <= lastPlacementColumnIndex; columnIndex += 1) {
                placementGrid[columnIndex][rowIndex] = true;
            }
        });

        return { positionedItemsByStartingPeriod, positionedItemsByContinueePeriod };
    }

    private findFreeRowIndex(placementGrid: readonly boolean[][], firstColumnIndex: number, lastColumnIndex: number): number {
        const columns = placementGrid.slice(firstColumnIndex, lastColumnIndex + 1);
        const isRowFree = (rowIndex: number) => columns.every(column => !column[rowIndex]);
        let rowIndex = 0;
        while (!isRowFree(rowIndex)) {
            rowIndex += 1;
        }
        return rowIndex;
    }
}
